import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { FiPlus, FiEdit2, FiTrash2, FiPackage, FiDollarSign, FiShoppingBag, FiAlertCircle } from 'react-icons/fi';
import './AdminPage.css';

const CATEGORIES = ['bracelets','necklaces','rings','hand-chains'];
const EMPTY_PRODUCT = { name:'', description:'', price:'', salePrice:'', category:'bracelets', images:'', stock:'', isFeatured:false };

const AdminPage = () => {
  const [tab, setTab] = useState('dashboard');
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({ total:0, revenue:0, pending:0, outOfStock:0 });
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [form, setForm] = useState(EMPTY_PRODUCT);

  useEffect(() => {
    if (tab === 'dashboard' || tab === 'products') fetchProducts();
    if (tab === 'dashboard' || tab === 'orders') fetchOrders();
  }, [tab]);

  const fetchProducts = async () => {
    try {
      const res = await api.get('/products?limit=100');
      const prods = res.data.products;
      setProducts(prods);
      setStats(s => ({ ...s, total: prods.length, outOfStock: prods.filter(p => p.stock === 0).length }));
    } catch (err) { toast.error('Failed to load products'); }
  };

  const fetchOrders = async () => {
    try {
      const res = await api.get('/orders');
      const ords = res.data.orders;
      setOrders(ords);
      const revenue = ords.filter(o => o.paymentStatus === 'paid').reduce((s,o) => s + o.totalAmount, 0);
      const pending = ords.filter(o => o.deliveryStatus === 'processing').length;
      setStats(s => ({ ...s, revenue, pending }));
    } catch (err) { toast.error('Failed to load orders'); }
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...form,
        price: parseFloat(form.price),
        salePrice: form.salePrice ? parseFloat(form.salePrice) : null,
        stock: parseInt(form.stock),
        images: form.images.split('\n').map(u => u.trim()).filter(Boolean),
      };
      if (editProduct) {
        await api.put(`/products/${editProduct._id}`, payload);
        toast.success('Product updated');
      } else {
        await api.post('/products', payload);
        toast.success('Product created');
      }
      setShowForm(false); setEditProduct(null); setForm(EMPTY_PRODUCT);
      fetchProducts();
    } catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  };

  const handleEdit = (p) => {
    setEditProduct(p);
    setForm({ ...p, images: p.images.join('\n'), price: p.price, salePrice: p.salePrice || '', stock: p.stock });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try { await api.delete(`/products/${id}`); toast.success('Deleted'); fetchProducts(); }
    catch (err) { toast.error('Delete failed'); }
  };

  const handleStatusUpdate = async (orderId, deliveryStatus) => {
    try {
      await api.put(`/orders/${orderId}/status`, { deliveryStatus });
      toast.success('Status updated');
      fetchOrders();
    } catch (err) { toast.error('Update failed'); }
  };

  return (
    <div className="admin-page">
      <div className="admin-sidebar desktop-only">
        <div className="admin-logo">
          <span className="logo-text">STONARI</span>
          <span style={{fontSize:'9px',letterSpacing:'0.2em',color:'#c9a96e'}}>ADMIN</span>
        </div>
        <nav>
          {['dashboard','products','orders'].map(t => (
            <button key={t} className={tab === t ? 'active' : ''} onClick={() => setTab(t)}>
              {t === 'dashboard' ? <FiShoppingBag /> : t === 'products' ? <FiPackage /> : <FiDollarSign />}
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </nav>
      </div>

      <div className="admin-bottom-nav mobile-only">
        {['dashboard','products','orders'].map(t => (
          <button key={t} className={tab === t ? 'active' : ''} onClick={() => setTab(t)}>
            {t === 'dashboard' ? <FiShoppingBag /> : t === 'products' ? <FiPackage /> : <FiDollarSign />}
            <span>{t.charAt(0).toUpperCase() + t.slice(1)}</span>
          </button>
        ))}
      </div>

      <div className="admin-content">
        {/* Dashboard */}
        {tab === 'dashboard' && (
          <div>
            <h1>Dashboard</h1>
            <div className="stats-grid">
              {[
                { label:'Total Products', value:stats.total, icon:<FiPackage /> },
                { label:'Total Revenue', value:`₹${stats.revenue.toFixed(0)}`, icon:<FiDollarSign /> },
                { label:'Pending Orders', value:stats.pending, icon:<FiShoppingBag /> },
                { label:'Out of Stock', value:stats.outOfStock, icon:<FiAlertCircle /> },
              ].map(s => (
                <div key={s.label} className="stat-card">
                  <span className="stat-icon">{s.icon}</span>
                  <p className="stat-label">{s.label}</p>
                  <h2 className="stat-value">{s.value}</h2>
                </div>
              ))}
            </div>
            <h2 style={{marginTop:'40px',marginBottom:'20px',fontFamily:'Cormorant Garamond,serif'}}>Recent Orders</h2>
            <OrdersTable orders={orders.slice(0,10)} onStatusUpdate={handleStatusUpdate} />
          </div>
        )}

        {/* Products */}
        {tab === 'products' && (
          <div>
            <div className="admin-toolbar">
              <h1>Products</h1>
              <button className="btn btn-primary desktop-only" onClick={() => { setShowForm(true); setEditProduct(null); setForm(EMPTY_PRODUCT); }}>
                <FiPlus /> Add Product
              </button>
            </div>

            {!showForm && (
              <button className="fab-add mobile-only" onClick={() => { setShowForm(true); setEditProduct(null); setForm(EMPTY_PRODUCT); }}>
                <FiPlus size={24} />
              </button>
            )}

            {showForm && (
              <form className="product-form" onSubmit={handleSubmit}>
                <h2>{editProduct ? 'Edit Product' : 'Add New Product'}</h2>
                <div className="form-grid">
                  <div className="form-group full-col">
                    <label className="form-label">Product Name *</label>
                    <input className="form-input" name="name" value={form.name} onChange={handleFormChange} required />
                  </div>
                  <div className="form-group full-col">
                    <label className="form-label">Description *</label>
                    <textarea className="form-input" name="description" value={form.description} onChange={handleFormChange} rows={4} required style={{resize:'vertical'}} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Price (₹) *</label>
                    <input className="form-input" name="price" type="number" value={form.price} onChange={handleFormChange} required min="0" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Sale Price (₹)</label>
                    <input className="form-input" name="salePrice" type="number" value={form.salePrice} onChange={handleFormChange} min="0" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Category *</label>
                    <select className="form-select" name="category" value={form.category} onChange={handleFormChange}>
                      {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Stock Quantity *</label>
                    <input className="form-input" name="stock" type="number" value={form.stock} onChange={handleFormChange} required min="0" />
                  </div>
                  <div className="form-group full-col">
                    <label className="form-label">Cloudinary Image URLs (one per line) *</label>
                    <textarea className="form-input" name="images" value={form.images} onChange={handleFormChange} rows={4} placeholder="https://res.cloudinary.com/..." style={{resize:'vertical'}} />
                  </div>
                  <div className="form-group">
                    <label style={{display:'flex',alignItems:'center',gap:'8px',cursor:'pointer'}}>
                      <input type="checkbox" name="isFeatured" checked={form.isFeatured} onChange={handleFormChange} />
                      <span className="form-label" style={{margin:0}}>Featured Product</span>
                    </label>
                  </div>
                </div>
                <div className="form-actions-sticky">
                  <button className="btn btn-outline" type="button" onClick={() => { setShowForm(false); setEditProduct(null); }}>Cancel</button>
                  <button className="btn btn-primary" type="submit" disabled={loading}>{loading ? 'Saving...' : editProduct ? 'Update Product' : 'Add Product'}</button>
                </div>
              </form>
            )}

            <div className="products-table desktop-only">
              <table>
                <thead>
                  <tr><th>Product</th><th>Category</th><th>Price</th><th>Stock</th><th>Featured</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {products.map(p => (
                    <tr key={p._id}>
                      <td>
                        <div style={{display:'flex',alignItems:'center',gap:'12px'}}>
                          {p.images[0] && <img src={p.images[0]} alt={p.name} style={{width:'48px',height:'60px',objectFit:'cover',borderRadius:'4px'}} />}
                          <span style={{fontWeight:500}}>{p.name}</span>
                        </div>
                      </td>
                      <td><span className="tag">{p.category}</span></td>
                      <td>₹{p.price}{p.salePrice && <span style={{color:'#c9a96e',marginLeft:'6px'}}>→ ₹{p.salePrice}</span>}</td>
                      <td><span style={{color: p.stock === 0 ? '#e05252' : '#52b788', fontWeight:600}}>{p.stock}</span></td>
                      <td>{p.isFeatured ? '✓' : '—'}</td>
                      <td>
                        <div style={{display:'flex',gap:'8px'}}>
                          <button className="icon-action" onClick={() => handleEdit(p)}><FiEdit2 size={14} /></button>
                          <button className="icon-action danger" onClick={() => handleDelete(p._id)}><FiTrash2 size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mobile-products-list mobile-only">
              {products.map(p => (
                <div key={p._id} className="mobile-product-card">
                  <img src={p.images[0] || 'https://via.placeholder.com/60'} alt={p.name} className="mpc-img" />
                  <div className="mpc-info">
                    <div className="mpc-title">{p.name}</div>
                    <div className="mpc-price">
                      ₹{p.price}{p.salePrice && <span style={{color:'#c9a96e',marginLeft:'6px'}}>→ ₹{p.salePrice}</span>}
                    </div>
                    <div className="mpc-stock">
                      Stock: <span style={{color: p.stock === 0 ? '#e05252' : '#52b788', fontWeight:600}}>{p.stock}</span>
                    </div>
                  </div>
                  <div className="mpc-actions">
                    <button className="icon-action large-tap" onClick={() => handleEdit(p)}><FiEdit2 size={18} /></button>
                    <button className="icon-action danger large-tap" onClick={() => handleDelete(p._id)}><FiTrash2 size={18} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === 'orders' && (
          <div>
            <h1>All Orders</h1>
            <OrdersTable orders={orders} onStatusUpdate={handleStatusUpdate} />
          </div>
        )}
      </div>
    </div>
  );
};

const OrdersTable = ({ orders, onStatusUpdate }) => {
  const [expandedId, setExpandedId] = useState(null);

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return '#f5a623';
      case 'processing': return '#4a90e2';
      case 'packed': return '#4a90e2';
      case 'shipped': return '#9013fe';
      case 'out_for_delivery': return '#9013fe';
      case 'delivered': return '#52b788';
      case 'cancelled': return '#d0021b';
      default: return '#999';
    }
  };

  return (
    <>
      <div className="products-table desktop-only">
        <table>
          <thead>
            <tr><th>Order ID</th><th>Customer</th><th>Amount</th><th>Payment</th><th>Delivery Status</th><th>Date</th><th>Action</th></tr>
          </thead>
          <tbody>
            {orders.map(o => (
              <tr key={o._id}>
                <td><strong>{o.orderId}</strong></td>
                <td>{o.user?.name || '—'}<br /><small style={{color:'#9a9a9a'}}>{o.user?.email}</small></td>
                <td>₹{o.totalAmount?.toFixed(0)}</td>
                <td><span className={`status-pill ${o.paymentStatus}`}>{o.paymentStatus}</span></td>
                <td>
                  <select
                    className="status-select"
                    value={o.deliveryStatus}
                    onChange={(e) => onStatusUpdate(o._id, e.target.value)}
                  >
                    {['processing','packed','shipped','out_for_delivery','delivered','cancelled'].map(s => (
                      <option key={s} value={s}>{s.replace(/_/g,' ')}</option>
                    ))}
                  </select>
                </td>
                <td>{new Date(o.createdAt).toLocaleDateString('en-IN')}</td>
                <td><a href={`/track/${o.orderId}`} target="_blank" rel="noreferrer" style={{color:'#c9a96e',fontSize:'12px'}}>Track</a></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mobile-orders-list mobile-only">
        {orders.map(o => (
          <div key={o._id} className="mobile-order-card" onClick={() => toggleExpand(o._id)}>
            <div className="moc-header">
              <div className="moc-id-name">
                <strong>#{o.orderId.slice(-6)}</strong>
                <span>{o.user?.name || '—'}</span>
              </div>
              <div className="moc-amount">₹{o.totalAmount?.toFixed(0)}</div>
            </div>
            <div className="moc-status">
              <span className="moc-badge" style={{ backgroundColor: getStatusColor(o.deliveryStatus) }}>
                {o.deliveryStatus.replace(/_/g, ' ')}
              </span>
            </div>
            
            {expandedId === o._id && (
              <div className="moc-details" onClick={(e) => e.stopPropagation()}>
                <div className="moc-detail-row">
                  <span>Payment:</span>
                  <span className={`status-pill ${o.paymentStatus}`}>{o.paymentStatus}</span>
                </div>
                <div className="moc-detail-row">
                  <span>Date:</span>
                  <span>{new Date(o.createdAt).toLocaleDateString('en-IN')}</span>
                </div>
                <div className="moc-detail-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '8px' }}>
                  <span>Update Status:</span>
                  <select
                    className="status-select moc-select"
                    value={o.deliveryStatus}
                    onChange={(e) => onStatusUpdate(o._id, e.target.value)}
                  >
                    {['processing','packed','shipped','out_for_delivery','delivered','cancelled'].map(s => (
                      <option key={s} value={s}>{s.replace(/_/g,' ')}</option>
                    ))}
                  </select>
                </div>
                <div className="moc-actions">
                  <a href={`/track/${o.orderId}`} target="_blank" rel="noreferrer" className="btn btn-outline" style={{width:'100%', display:'flex', justifyContent:'center'}}>Track Order</a>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
};

export default AdminPage;
